import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import StatusBar from "./components/StatusBar";
import AssetOverview from "./components/AssetOverview";
import AIDecisionCard from "./components/AIDecisionCard";
import Watchlist from "./components/Watchlist";
import PositionSnapshot from "./components/PositionSnapshot";
import PullToRefresh from "@/components/base/PullToRefresh";
import DashboardSkeleton from "@/components/base/Skeleton";
import {
  listSessions,
  getAssetCurve,
  listDebateRounds,
  listAnalystViews,
  listHoldings,
  listRecentBroadcasts,
  getAlphaStats,
  type AnalystView,
  type DebateRound,
  type SessionHolding,
} from "@/api/alpha";
import { getSimAccount } from "@/api/sim";
import MarketNewsSection from "./components/MarketNewsSection";

interface Perspective {
  label: string;
  verdict: string;
}

interface Decision {
  stockName: string;
  stockCode: string;
  action: string;
  actionType: "buy" | "sell" | "hold";
  confidence: number;
  suggestedPosition: number;
  reason: string;
  perspectives: Perspective[];
}

interface PositionItem {
  name: string;
  code: string;
  price: number;
  cost: number;
  profitRate: number;
  shares: number;
}

interface DashboardData {
  totalAssets: number;
  totalReturnRate: number;
  todayProfit: number;
  floatingProfit: number;
  netValueHistory: number[];
  decisions: Decision[];
  positions: PositionItem[];
  phaseLabel: string;
  statusText: string;
  broadcastMessage: string;
}

// agentType (fundamental|technical|sentiment|news) → AI 看盘 4 视角标签 + 顺序
const PERSPECTIVE_ORDER = ["fundamental", "technical", "sentiment", "news"] as const;
const PERSPECTIVE_LABEL: Record<string, string> = {
  fundamental: "基本面",
  technical: "技术面",
  sentiment: "情绪面",
  news: "新闻面",
};

// finalAction (买入|卖出|持有|观望) → 卡片色块类型
function actionTypeOf(action: string): "buy" | "sell" | "hold" {
  if (/买|加|建仓|进入/.test(action)) return "buy";
  if (/卖|减|退|清/.test(action)) return "sell";
  return "hold";
}

const DEFAULT_BROADCAST =
  "AI 模拟盘运行中，正在持续跟踪 A 股行情并生成模拟研判。本产品仅供学习参考，不构成投资建议。";

function buildPerspectives(views: AnalystView[]): Perspective[] {
  const byType = new Map<string, AnalystView>();
  for (const v of views) {
    // 后端返回的是「每个 agentType 的最新一条」，去重保险起见取首次出现
    if (!byType.has(v.agentType)) byType.set(v.agentType, v);
  }
  const out: Perspective[] = [];
  for (const t of PERSPECTIVE_ORDER) {
    const v = byType.get(t);
    if (!v) continue;
    out.push({
      label: PERSPECTIVE_LABEL[t] || t,
      verdict: (v.verdict || "").trim(),
    });
  }
  return out;
}

function buildDecisions(rounds: DebateRound[], perspectives: Perspective[]): Decision[] {
  // 后端按 id asc（最旧在前）返回；AI 看盘要最新研判在最前面。
  const newestFirst = [...rounds].reverse().slice(0, 6);
  return newestFirst.map((r) => ({
    stockName: r.ticker,
    stockCode: r.tickerCode,
    action: r.finalAction,
    actionType: actionTypeOf(r.finalAction),
    confidence: Math.max(0, Math.min(5, r.confidence || 0)),
    suggestedPosition: r.suggestedPosition || 0,
    reason: r.reasoning || "",
    // 4 视角是 session 级别的最新分析快照，复用到每张决策卡。
    perspectives,
  }));
}

function buildThinking(views: AnalystView[], rounds: DebateRound[]): string[] {
  const lines: string[] = [];
  const byType = new Map<string, AnalystView>();
  for (const v of views) if (!byType.has(v.agentType)) byType.set(v.agentType, v);
  for (const t of PERSPECTIVE_ORDER) {
    const v = byType.get(t);
    if (!v) continue;
    const label = PERSPECTIVE_LABEL[t] || t;
    const msg = (v.currentMessage || v.verdict || "").trim();
    if (msg) lines.push(`${label}分析师 · ${v.verdict || "研判"}：${msg}`);
  }
  // 追加最近几轮综合决策，营造「思考 → 结论」的推送流。
  const newest = [...rounds].reverse().slice(0, 4);
  for (const r of newest) {
    const reason = (r.reasoning || "").trim();
    if (reason) {
      lines.push(`综合研判 · ${r.ticker} ${r.finalAction}（置信 ${r.confidence || 0}/5）：${reason}`);
    }
  }
  return lines;
}

function buildPositions(holdings: SessionHolding[]): PositionItem[] {
  return holdings
    .filter((h) => h.shares > 0)
    .map((h) => ({
      name: h.ticker,
      code: h.tickerCode,
      price: h.currentPrice,
      cost: h.avgCost,
      profitRate: h.pnlPct, // 后端已是百分数（pnl/costBasis*100），与组件预期一致
      shares: h.shares,
    }));
}

function statusTextOf(status: string): string {
  switch (status) {
    case "running":
      return "AI 运行中";
    case "paused":
      return "已暂停";
    case "ended":
    case "completed":
      return "已结束";
    default:
      return status || "—";
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [currentDecisionIdx, setCurrentDecisionIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [hasSession, setHasSession] = useState(true);
  const [sessionId, setSessionId] = useState<number>(0);
  const [data, setData] = useState<DashboardData | null>(null);

  const loadData = useCallback(async () => {
    setError("");
    // 平台级统计（盘中/休市）与会话列表，互不依赖，可并发。
    const [sessionsRes, statsRes] = await Promise.all([
      listSessions(),
      getAlphaStats().catch(() => null),
    ]);
    const sessions = sessionsRes.sessions || [];
    const s = sessions.find((x) => x.status === "running") || sessions[0];
    if (!s) {
      setHasSession(false);
      setSessionId(0);
      setData(null);
      return;
    }
    setHasSession(true);
    setSessionId(s.id);

    const [simAccRes, curveRes, roundsRes, viewsRes, holdingsRes, broadcastsRes] = await Promise.all([
      getSimAccount().catch(() => null),
      getAssetCurve(s.id).catch(() => null),
      listDebateRounds(s.id).catch(() => ({ rounds: [] as DebateRound[] })),
      listAnalystViews(s.id).catch(() => ({ analysts: [] as AnalystView[] })),
      listHoldings(s.id).catch(() => ({ holdings: [] as SessionHolding[] })),
      listRecentBroadcasts(1).catch(() => ({ broadcasts: [] })),
    ]);

    const curve = curveRes?.curve || [];
    const stats = curveRes?.stats;
    const rounds = roundsRes.rounds || [];
    const views = viewsRes.analysts || [];
    const holdings = holdingsRes.holdings || [];
    const broadcasts = broadcastsRes.broadcasts || [];

    // 今日盈亏 = 净值曲线最后两点之差
    let todayProfit = 0;
    if (curve.length >= 2) {
      todayProfit = curve[curve.length - 1].totalAsset - curve[curve.length - 2].totalAsset;
    }
    const netValueHistory =
      curve.length >= 2 ? curve.map((p) => p.totalAsset) : [s.fundAmount, s.totalAsset];

    const perspectives = buildPerspectives(views);

    const realTotalAsset = simAccRes?.totalValue ?? s.totalAsset;
    const realPnL = simAccRes?.totalPnL ?? s.floatingPnL;

    setData({
      totalAssets: realTotalAsset,
      totalReturnRate: Number((stats?.totalReturn ?? 0).toFixed(2)),
      todayProfit,
      floatingProfit: realPnL,
      netValueHistory,
      decisions: buildDecisions(rounds, perspectives),
      positions: buildPositions(holdings),
      phaseLabel: statsRes?.isTradingHoursNow ? "盘中" : "休市",
      statusText: statusTextOf(s.status),
      broadcastMessage: broadcasts[0]?.content || DEFAULT_BROADCAST,
    });
    setCurrentDecisionIdx(0);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await loadData();
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "加载失败");
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadData]);

  // ── Live polling: refresh totalAsset + positions every 1s ──
  // Backend enrichAccount() reads each position's currentPrice from Redis,
  // so totalValue reflects live market prices without a full page reload.
  // Tab-hidden tabs skip the refresh (visibilitychange) so backgrounded
  // tabs don't burn API calls.
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!sessionId) return;
    if (!initialLoadDone.current && isLoading) return;
    initialLoadDone.current = true;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      Promise.all([
        getSimAccount().catch(() => null),
        listHoldings(sessionId).catch(() => ({ holdings: [] as SessionHolding[] })),
      ]).then(([simAcc, holdingsRes]) => {
        const positions = buildPositions(holdingsRes?.holdings ?? []);
        const totalAssets = simAcc?.totalValue;
        const floatingProfit = simAcc?.totalPnL;
        setData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            totalAssets: totalAssets ?? prev.totalAssets,
            floatingProfit: floatingProfit ?? prev.floatingProfit,
            positions,
          };
        });
      }).catch(() => {});
    };
    const timer = setInterval(tick, 1_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [sessionId, isLoading]);

  const decisions = data?.decisions ?? [];
  const currentDecision = decisions[currentDecisionIdx];

  const handleSwipe = (direction: "left" | "right") => {
    if (direction === "left" && currentDecisionIdx < decisions.length - 1) {
      setCurrentDecisionIdx(currentDecisionIdx + 1);
    } else if (direction === "right" && currentDecisionIdx > 0) {
      setCurrentDecisionIdx(currentDecisionIdx - 1);
    }
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "刷新失败");
    } finally {
      setRefreshing(false);
    }
  }, [loadData]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-8 text-center gap-3">
        <i className="ri-wifi-off-line text-3xl text-foreground-300" />
        <p className="text-sm text-foreground-500">{error}</p>
        <button
          onClick={() => {
            setIsLoading(true);
            loadData()
              .catch((e) => setError(e instanceof Error ? e.message : "加载失败"))
              .finally(() => setIsLoading(false));
          }}
          className="mt-1 px-4 py-2 bg-primary-500 text-white rounded-xl text-xs font-medium cursor-pointer"
        >
          重试
        </button>
      </div>
    );
  }

  if (!hasSession || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-8 text-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center">
          <i className="ri-rocket-2-line text-2xl text-primary-500" />
        </div>
        <p className="text-sm font-semibold text-foreground-900">还没有运行中的模拟盘</p>
        <p className="text-xs text-foreground-500 leading-relaxed">
          先去选择一个 AI 策略并开启模拟盘，AI 看盘就会展示实时研判与资产走势。
        </p>
        <button
          onClick={() => navigate("/strategies")}
          className="mt-1 px-5 py-2.5 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
        >
          去开启模拟盘
        </button>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="pb-4">
        {/* 顶部状态条 */}
        <StatusBar phaseLabel={data.phaseLabel} statusText={data.statusText} />

        {/* 资产总览 */}
        <AssetOverview
          totalAssets={data.totalAssets}
          totalReturnRate={data.totalReturnRate}
          todayProfit={data.todayProfit}
          floatingProfit={data.floatingProfit}
          netValueHistory={data.netValueHistory}
        />

        {/* AI 决策卡 — 可左右翻看 */}
        {decisions.length > 0 && currentDecision && (
          <div className="mt-2">
            <div className="px-4 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground-950">
                  AI 实时研判
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
              </div>
              <div className="flex items-center gap-1">
                {decisions.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      i === currentDecisionIdx
                        ? "bg-primary-500 w-3"
                        : "bg-background-300"
                    }`}
                  />
                ))}
              </div>
            </div>

            <AIDecisionCard
              stockName={currentDecision.stockName}
              stockCode={currentDecision.stockCode}
              action={currentDecision.action}
              actionType={currentDecision.actionType}
              confidence={currentDecision.confidence}
              suggestedPosition={currentDecision.suggestedPosition || 0}
              reason={currentDecision.reason}
              perspectives={currentDecision.perspectives}
              onSwipe={handleSwipe}
              onClick={() => navigate(`/decision/${currentDecisionIdx}`)}
            />

            <div className="flex justify-center gap-3 mt-1.5">
              <button
                onClick={() => handleSwipe("right")}
                disabled={currentDecisionIdx === 0}
                className={`w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-all ${
                  currentDecisionIdx === 0
                    ? "text-foreground-300"
                    : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                <i className="ri-arrow-left-s-line text-sm" />
              </button>
              <span className="text-[10px] text-foreground-400 leading-6">
                {currentDecisionIdx + 1} / {decisions.length}
              </span>
              <button
                onClick={() => handleSwipe("left")}
                disabled={currentDecisionIdx === decisions.length - 1}
                className={`w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-all ${
                  currentDecisionIdx === decisions.length - 1
                    ? "text-foreground-300"
                    : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* 自选 */}
        <Watchlist sessionId={sessionId} />

        {/* 持仓快照 */}
        <PositionSnapshot positions={data.positions} />

        {/* 市场资讯 */}
        <MarketNewsSection />

        {/* 时段播报 — 简洁条 */}
        <div className="px-4 mt-3">
          <div className="bg-background-50 rounded-xl px-3.5 py-2.5 flex items-center gap-2">
            <i className="ri-volume-up-line text-foreground-400 text-xs flex-shrink-0" />
            <p className="text-[11px] text-foreground-500 leading-relaxed">
              {data.broadcastMessage}
            </p>
          </div>
        </div>

        {/* Refresh toast */}
        {refreshing && (
          <div className="px-4 mt-2">
            <div className="bg-primary-50 rounded-xl px-3 py-2 flex items-center gap-2 animate-fade-in">
              <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-[10px] text-primary-600">数据刷新中...</span>
            </div>
          </div>
        )}

        {/* 免责声明 */}
        <div className="px-4 mt-3 mb-4">
          <p className="text-[10px] text-foreground-400 text-center">
            模拟 · 仅供学习参考，不构成投资建议
          </p>
        </div>
      </div>

    </PullToRefresh>
  );
}
